/* eslint-disable react-refresh/only-export-components */
import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import AppLayout from "../pages/app/AppLayout";
import AuthLayout from "../pages/auth/AuthLayout";
import { rootRoute } from "./__root";
import { useStore } from "../store/store";
import { getCookie } from "@/utils/cookies";
import RoleManagement from "@/pages/app/RoleManagement/RoleManagement";
import { PermissionAssignmentView } from "@/pages/app/RoleManagement/components/PermissionAssignmentView";
import { EmployeePermissionAssignmentView } from "@/pages/app/employee-management/components/EmployeePermissionAssignmentView";
import UnAuthorized from "@/pages/app/UnAuthorized";
import RoleSelect from "@/pages/auth/role-select/RoleSelect";
import RoutePermissionGuard from "@/components/RoutePermissionGuard";
import FeatureFlagGuard from "@/components/FeatureFlagGuard";
import {
  AnalyticsDashboardDetailAccessGuard,
  AnalyticsDashboardsListAccessGuard,
} from "@/pages/app/analytics/components/AnalyticsDashboardAccessGuard";
// Lazily loaded routes
const AgentPerformanceListingV2 = lazyRouteComponent(
  () => import("../pages/app/agent-performance/AgentPerformanceListingV2"),
);
const Login = lazyRouteComponent(() => import("../pages/auth/login/Login"));
const ForgotPassword = lazyRouteComponent(
  () => import("../pages/auth/forgot-password/ForgotPassword"),
);
const NoPageFound = lazyRouteComponent(
  () => import("../pages/app/no-page-found/NoPageFound"),
);
const Forbidden = lazyRouteComponent(
  () => import("../pages/app/forbidden/Forbidden"),
);
const EvaluationDashboard = lazyRouteComponent(
  () => import("../pages/app/evaluation-dashboard/EvaluationDashboard"),
);
const EvaluationList = lazyRouteComponent(
  () => import("../pages/app/evaluation-list/EvaluationList"),
);
const EvaluationDetails = lazyRouteComponent(
  () => import("../pages/app/evaluation-details/EvaluationDetails"),
);
const ManageTemplate = lazyRouteComponent(
  () => import("../pages/app/manage-template/ManageTemplate"),
);
const ViewEditTemplate = lazyRouteComponent(
  () => import("../pages/app/manage-template/ViewEditTemplate"),
);
const Branding = lazyRouteComponent(
  () => import("../pages/app/branding/Branding"),
);
const EmployeeManagement = lazyRouteComponent(
  () => import("../pages/app/employee-management/EmployeeManagement"),
);
const EmployeeAttributes = lazyRouteComponent(
  () => import("../pages/app/employee-attributes/EmployeeAttributes"),
);
const DataIngestionList = lazyRouteComponent(
  () => import("../pages/app/data-ingestion/DataIngestionListing"),
);
const DataIngestionAddEntity = lazyRouteComponent(
  () => import("../pages/app/data-ingestion/DataIngestionAddEntity"),
);
const DataIngestionEditEntity = lazyRouteComponent(
  () => import("../pages/app/data-ingestion/DataIngestionEditEntity"),
);
const PushApiClientsList = lazyRouteComponent(
  () => import("../pages/app/push-api-clients/PushApiClientsListing"),
);
const PushApiClientHistory = lazyRouteComponent(
  () => import("../pages/app/push-api-clients/PushApiClientHistory"),
);
const ManageUsers = lazyRouteComponent(
  () => import("../pages/app/manage-users/ManageUsers"),
);
const ChangePassword = lazyRouteComponent(
  () => import("../pages/app/change-password/ChangePassword"),
);
const EntityScaleRating = lazyRouteComponent(
  () => import("../pages/app/entity-config/CreateEntityScaleRating"),
);
const TopViolationsByCategory = lazyRouteComponent(
  () => import("../pages/app/report/TopViolationsByCategory"),
);
const UserConfigurationDashboard = lazyRouteComponent(
  () => import("../pages/app/UserConfigurationDashboard"),
);

const ReportsDashboard = lazyRouteComponent(
  () => import("../pages/app/report/ReportsDashboard"),
);

const NanoLearningDashboard = lazyRouteComponent(
  () => import("../pages/app/learning/NanoLearningDashboard"),
);

const PersonalizedCoachingInsights = lazyRouteComponent(
  () =>
    import("../pages/app/personalized-coaching-insights/PersonalizedCoachingInsights"),
);

const OverAllSentimentAnalysisReport = lazyRouteComponent(
  () => import("../pages/app/report/OverAllSentimentAnalysisReport"),
);

const CreateEntity = lazyRouteComponent(
  () => import("../pages/app/create-entity/CreateEntity"),
);

const AgentPerformanceDetails = lazyRouteComponent(
  () => import("../pages/app/agent-performance/AgentPerformanceDetailsNew"),
);

const AgentPerformanceAllAgentListPage = lazyRouteComponent(
  () =>
    import("../pages/app/agent-performance/AgentPerformanceAllAgentListPage"),
);

const ResetPassword = lazyRouteComponent(
  () => import("../pages/auth/reset-password/ResetPassword"),
);
const AgentRankingConfiguration = lazyRouteComponent(
  () =>
    import("../pages/app/configuration/agent-ranking-configuration/AgentRankingConfiguration"),
);
const STTEngineConfigurations = lazyRouteComponent(
  () =>
    import("../pages/admin/stt-engine-configuration/STTEngineConfigurations"),
);

const CampaignTeamsReport = lazyRouteComponent(
  () => import("../pages/app/report/CampaignTeamsReport"),
);

const CampaignTeamDetail = lazyRouteComponent(
  () => import("../pages/app/report/CampaignTeamDetail"),
);

const SupervisorTeamsReport = lazyRouteComponent(
  () => import("../pages/app/report/SupervisorTeamsReport"),
);

const SupervisorTeamDetail = lazyRouteComponent(
  () => import("../pages/app/report/SupervisorTeamDetail"),
);

const OrganizationHierarchy = lazyRouteComponent(
  () => import("../pages/app/organization-hierarchy/OrganizationHierarchy"),
);
const SelectionCriteria = lazyRouteComponent(
  () => import("../pages/app/selection-criteria/SelectionCriteria"),
);
const CallProcessingQueuePlanner = lazyRouteComponent(
  () =>
    import("../pages/app/call-processing-queue-planner/CallProcessingQueuePlanner"),
);

const PiiConfiguration = lazyRouteComponent(
  () => import("../pages/app/pii-configuration/PiiConfiguration"),
);

const PiiEntity = lazyRouteComponent(
  () => import("../pages/app/pii-configuration/PiiEntity"),
);

const PiiPattern = lazyRouteComponent(
  () => import("../pages/app/pii-configuration/PiiPattern"),
);

const PiiDenyTerm = lazyRouteComponent(
  () => import("../pages/app/pii-configuration/PiiDenyTerm"),
);

const RenewPassword = lazyRouteComponent(
  () => import("../pages/auth/renew-password/RenewPassword"),
);

const OnboardUser = lazyRouteComponent(
  () => import("../pages/auth/onboard-user/OnboardUser"),
);
const InviteVerification = lazyRouteComponent(
  () => import("../pages/auth/invite-verification/InviteVerification"),
);

const EmployeeToUserVerification = lazyRouteComponent(
  () =>
    import("../pages/auth/employee-to-user-verification/EmployeeToUserVerification"),
);

const Services = lazyRouteComponent(
  () => import("../pages/app/services/Services"),
);

const Campaigns = lazyRouteComponent(
  () => import("../pages/app/campaign/Campaign"),
);

const CustomerInsights = lazyRouteComponent(
  () => import("../pages/app/report/CustomerInsights"),
);
const CustomerInsightsStaticCharts = lazyRouteComponent(
  () => import("../pages/app/report/CustomerInsightsStaticCharts"),
);

const FailureCategoryCalls = lazyRouteComponent(
  () => import("../pages/app/failure-category-calls/FailureCategoryCalls"),
);

const NanoLearning = lazyRouteComponent(
  () => import("../pages/app/nano-learning/NanoLearning"),
);

const CreateNanoLearning = lazyRouteComponent(
  () => import("../pages/app/nano-learning/CreateNanoLearning"),
);

const EditNanoLearning = lazyRouteComponent(
  () => import("../pages/app/nano-learning/EditNanoLearning"),
);

const LearningLibrary = lazyRouteComponent(
  () => import("../pages/app/learning/LearningLibrary"),
);

const CategoryContent = lazyRouteComponent(
  () => import("../pages/app/learning/CategoryContent"),
);

const LearningLayout = lazyRouteComponent(
  () => import("../pages/app/learning/LearningLayout"),
);

const AnalyticsExplorer = lazyRouteComponent(
  () => import("../pages/app/analytics/AnalyticsExplorer"),
);

const AnalyticsDashboardsListPage = lazyRouteComponent(
  () => import("../pages/app/analytics/AnalyticsDashboardsListPage"),
);

const CreateDashboardPage = lazyRouteComponent(
  () => import("../pages/app/analytics/CreateDashboardPage"),
);

const AnalyticDashboardComposerPage = lazyRouteComponent(
  () => import("../pages/app/analytics/AnalyticDashboardComposerPage"),
);

const MetricDefinitionPage = lazyRouteComponent(
  () => import("../pages/app/analytics/components/MetricDefinitionPage"),
);

const MetricDefinitionForm = lazyRouteComponent(
  () => import("../pages/app/analytics/components/MetricDefinitionForm"),
);

const DerivedMetricForm = lazyRouteComponent(
  () => import("../pages/app/analytics/components/DerivedMetricForm"),
);

const DimensionDefinitionPage = lazyRouteComponent(
  () => import("../pages/app/analytics/components/DimensionDefinitionPage"),
);

// Admin Components
const AdminDashboard = lazyRouteComponent(
  () => import("../pages/admin/admin-dashbaord/AdminDashboard"),
);

const EntityFieldMapping = lazyRouteComponent(
  () => import("../pages/admin/entity-field-mapping/EntityFieldMapping"),
);

const WorkFLowBuilder = lazyRouteComponent(
  () => import("../pages/admin/workflow-builder/WorkFLowBuilder"),
);

const AdminDomains = lazyRouteComponent(
  () => import("../pages/admin/admin-domains/Domains"),
);

const AdminCampaigns = lazyRouteComponent(
  () => import("../pages/admin/admin-campaigns/AdminCampaigns"),
);

const AdminServices = lazyRouteComponent(
  () => import("../pages/admin/admin-services/AdminServices"),
);
const AdminCallSummary = lazyRouteComponent(
  () => import("../pages/admin/call-summary/CallSummary"),
);
// Admin - AI Engine Configurations
const AIEngineConfigurations = lazyRouteComponent(
  () =>
    import("../pages/admin/ai-engine-configurations/AIEngineConfigurations"),
);
const AgentPerformanceLeaderboard = lazyRouteComponent(
  () =>
    import("../pages/app/agent-performance/components/AgentPerformanceLeaderboard"),
);

const AIImprovementTrackingTable = lazyRouteComponent(
  () =>
    import("../pages/app/performance-insights/components/AIImprovementTrackingTable"),
);

// const hasTenantSubdomain = (): boolean => {
//   const { hostname } = window.location;

//   // Match the logic in `src/utils/env.ts` so the frontend and API base-url
//   // selection agree on what "tenant" means.
//   const isIpAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
//   const hostParts = hostname.split(".");
//   const subdomain = hostParts[0];
//   const skipSubdomains = ["app", "www", "admin", "api"];

//   // Tenant exists only when we have at least 2 parts (e.g. tenant.localhost)
//   // and the left-most label is not a reserved "app/www/admin/api" label.
//   return (
//     !isIpAddress &&
//     Boolean(subdomain) &&
//     !skipSubdomains.includes(subdomain) &&
//     hostParts.length > 1
//   );
// };

const appLayoutRoute = createRoute({
  beforeLoad: ({ context }) => {
    const hasToken =
      localStorage.getItem("access_token") ??
      getCookie("access_token") ??
      context.auth?.access_token;

    // If a user lands on the "main" domain (no tenant subdomain) while holding tokens,
    // those tokens won't be valid for tenant-scoped APIs. Force logout to avoid loaders
    // / half-rendered UI.
    // if (hasToken && !hasTenantSubdomain()) {
    //   const user = useStore.getState().user;
    //   const isAdmin = user?.is_superuser === true || user?.is_staff === true;

    //   // Allow admin/superuser to work from the main domain.
    //   if (!isAdmin) {
    //     useStore.getState().resetAuthStore();
    //     clearStore();
    //     redirect({
    //       to: "/auth/login",
    //       search: { logout: "true" },
    //       throw: true,
    //     });
    //   }
    // }

    if (!hasToken) {
      redirect({
        to: "/auth/login",
        throw: true,
      });
    }
  },
  getParentRoute: () => rootRoute,
  id: "app-layout",
  component: AppLayout,
});

const authLayoutRoute = createRoute({
  beforeLoad: () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isLogout = urlParams.get("logout") === "true";
    if (isLogout) {
      useStore.getState().resetAuthStore();
      return;
    }
  },
  getParentRoute: () => rootRoute,
  id: "auth-layout",
  component: AuthLayout,
});

export const evaluationDashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/evaluation-dashboard",
  component: () => (
    <FeatureFlagGuard requiredFlags={["evaluation-dashboard"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-evaluation-dashboard"]}
      >
        <EvaluationDashboard />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const evaluationListingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/evaluation-listing",
  component: () => (
    <FeatureFlagGuard requiredFlags={["evaluation-list"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-evaluation-list"]}>
        <EvaluationList />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const evaluationDetailsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/evaluation-details/$evaluationId",
  component: () => (
    <FeatureFlagGuard
      requiredFlags={["evaluation-details", "evaluation-dashboard"]}
    >
      <RoutePermissionGuard
        allowedPermissions={["can-view-ai-evaluation-details"]}
      >
        <EvaluationDetails />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const manageTemplateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/manage-template",
  component: () => (
    <FeatureFlagGuard requiredFlags={["template"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-evaluation-templates"]}
      >
        <ManageTemplate />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const manageTemplateEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/edit-template/$templateId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["template"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-evaluation-templates",
          "can-edit-templates",
        ]}
        checkAllPermissions={true}
      >
        <ViewEditTemplate />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const addManageTemplateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/add-template",
  component: () => (
    <FeatureFlagGuard requiredFlags={["template"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-evaluation-templates",
          "can-add-templates",
        ]}
        checkAllPermissions={true}
      >
        <ViewEditTemplate />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const manageUserRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/manage-users",
  component: () => (
    <FeatureFlagGuard requiredFlags={["users"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-manage-users"]}>
        <ManageUsers />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const failureCategoryCallsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/failed-category-calls/$categoryKey",
  component: () => (
    <FeatureFlagGuard requiredFlags={["failed-category-calls"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-failed-category-calls-list"]}
      >
        <FailureCategoryCalls />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const brandingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/branding/$entityId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["branding"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-configuration-branding"]}
      >
        <Branding />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const dataIngestionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/data-ingestion",
  component: () => (
    <FeatureFlagGuard requiredFlags={["data-ingestion"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-data-ingestion-profiles"]}
      >
        <DataIngestionList />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const dataIngestionAddEntityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/add-data-ingestion-configuration",
  component: () => (
    <FeatureFlagGuard requiredFlags={["data-ingestion"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-add-data-ingestion-profiles"]}
      >
        <DataIngestionAddEntity />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const dataIngestionEditEntityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/edit-data-ingestion-configuration/$entityInternalId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["data-ingestion"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-edit-data-ingestion-profiles"]}
      >
        <DataIngestionEditEntity />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const pushApiClientsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/push-api-clients",
  component: () => (
    <FeatureFlagGuard requiredFlags={["push-api-clients"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-data-ingestion-push-api-clients"]}
      >
        <PushApiClientsList />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const pushApiClientsHistoryRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/push-api-clients/history/$internalId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["push-api-clients"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-history-data-ingestion-push-api-clients",
        ]}
      >
        <PushApiClientHistory />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const agentPerformanceListingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/agent-performance-list",
  component: () => (
    <FeatureFlagGuard requiredFlags={["agent-performance-list"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-agent-performance-list",
          "can-view-reports",
        ]}
        checkAllPermissions={true}
      >
        <AgentPerformanceListingV2 />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const agentPerformanceDetailsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/agent-performance-details/$agentId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["agent-performance-dashboard"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-agent-performance-report-details"]}
      >
        <AgentPerformanceDetails />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const agentPerformanceDetailsListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/agent-performance-details-list/$groupInternalId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["agent-performance-list"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-agent-performance-list",
          "can-view-reports",
        ]}
        checkAllPermissions={true}
      >
        <AgentPerformanceAllAgentListPage />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});
export const piiConfigurationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/pii-configuration",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-pii-configuration"]}>
      <PiiConfiguration />
    </RoutePermissionGuard>
  ),
});

export const entityScaleRatingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/entity-scale-rating",
  component: () => (
    <FeatureFlagGuard requiredFlags={["entity-scale-rating"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-configuration-entity-scale-ratings"]}
      >
        <EntityScaleRating />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const organizationHierarchyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/organization-hierarchy",
  component: () => (
    <FeatureFlagGuard requiredFlags={["organization-hierarchy"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-organization-hierarchy"]}
      >
        <OrganizationHierarchy />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const piiEntityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/pii-entity",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-pii-entity"]}>
      <PiiEntity />
    </RoutePermissionGuard>
  ),
});

export const piiPatternRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/pii-pattern",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-pii-pattern"]}>
      <PiiPattern />
    </RoutePermissionGuard>
  ),
});

export const piiDenyTermRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/pii-denyterm",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-pii-denyterm"]}>
      <PiiDenyTerm />
    </RoutePermissionGuard>
  ),
});

export const selectionCriteriaRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/selection-criteria",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-selection-criteria"]}>
      <SelectionCriteria />
    </RoutePermissionGuard>
  ),
});

export const callProcessingQueuePlannerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/call-processing-queue-planner",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={["can-view-sampling-configurations"]}
    >
      <CallProcessingQueuePlanner />
    </RoutePermissionGuard>
  ),
});

export const servicesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/services",
  component: () => (
    <FeatureFlagGuard requiredFlags={["services"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-services"]}>
        <Services />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const employeeManagementRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/employee-management",
  component: () => (
    <FeatureFlagGuard requiredFlags={["employee-management"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-employee-list"]}>
        <EmployeeManagement />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const assignEmployeePermissionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/employee-management/assign-permission/$employeeId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["employee-management"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-permissions",
          "can-view-employee-assigned-permissions",
          "can-view-employee-available-permissions",
        ]}
      >
        <EmployeePermissionAssignmentView />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const employeeAttributesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/employee-attributes",
  component: () => (
    <FeatureFlagGuard requiredFlags={["employee-attributes"]}>
      <RoutePermissionGuard
        allowedPermissions={["can-view-configuration-employee-attributes"]}
      >
        <EmployeeAttributes />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const roleManagementRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/role-management",
  component: () => (
    <FeatureFlagGuard requiredFlags={["role-management"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-role-groups"]}>
        <RoleManagement />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const assignPermissionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/role-management/assign-permission/$roleId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["role-management"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-permissions"]}>
        <PermissionAssignmentView />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const campaignRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/campaign",
  component: () => (
    <FeatureFlagGuard requiredFlags={["campaign"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-campaigns"]}>
        <Campaigns />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const configDashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/configurations",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-configurations"]}>
      <UserConfigurationDashboard />
    </RoutePermissionGuard>
  ),
});

export const reportsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-reports"]}>
      <ReportsDashboard />
    </RoutePermissionGuard>
  ),
});

export const nanoLearningHubRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/nano-learning-hub",
  component: () => (
    <FeatureFlagGuard requiredFlags={["nano-learning"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-nano-learning"]}>
        <NanoLearningDashboard />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

// export const metaConfigurationRoute = createRoute({
//   getParentRoute: () => appLayoutRoute,
//   path: "/meta-configuration",
//   component: () => (
//     <RoutePermissionGuard allowedPermissions={[]}>
//       <MetadataConfiguration />
//     </RoutePermissionGuard>
//   ),
// });

export const agentRankingConfigurationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/agent-ranking-configuration",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={["can-view-agent-ranking-configuration"]}
    >
      <AgentRankingConfiguration />
    </RoutePermissionGuard>
  ),
});

// export const auditLogsReportRoute = createRoute({
//   getParentRoute: () => appLayoutRoute,
//   path: "/audit-logs-report",
//   component: () => (
//     <RoutePermissionGuard allowedPermissions={[]}>
//       <AuditLogsReport />
//     </RoutePermissionGuard>
//   ),
// });

export const createEntityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/create-entity",
  component: () => (
    <FeatureFlagGuard requiredFlags={["create-entity"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-create-entity"]}>
        <CreateEntity />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/forgot-password",
  component: ForgotPassword,
});

export const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/login",
  component: Login,
});
export const emptyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/",
  component: Login,
});
export const NoPageFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/404",
  component: NoPageFound,
});

export const MainLayoutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/app",
});
export const ForbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forbidden",
  component: Forbidden,
});

export const changePasswordRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/change-password",
  component: ChangePassword,
});

export const updatePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/update-password",
  component: ResetPassword,
});

export const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/reset-password",
  component: ResetPassword,
});

export const renewPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/renew-password",
  component: RenewPassword,
});

export const topViolationByCategoryRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/violations-by-category",
  component: () => (
    <FeatureFlagGuard requiredFlags={["violations-by-category"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-violations-by-category-report-list",
          "can-view-reports",
        ]}
        checkAllPermissions={true}
      >
        <TopViolationsByCategory />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const customerInsightsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/customer-insights",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={[
        "can-view-customer-insights-reports",
        "can-view-reports",
      ]}
      checkAllPermissions={true}
    >
      <CustomerInsights />
    </RoutePermissionGuard>
  ),
});

export const customerInsightsStaticChartsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/customer-insights-v2",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={[
        "can-view-customer-insights-static-reports",
        "can-view-reports",
      ]}
      checkAllPermissions={true}
    >
      <CustomerInsightsStaticCharts />
    </RoutePermissionGuard>
  ),
});

export const personalizedCoachingInsightsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/personalized-coaching-insights",
  component: () => (
    <FeatureFlagGuard requiredFlags={["personalized-coaching-insights"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-personalized-coaching-insights-reports",
          "can-view-reports",
          "can-view-employee-agent-list",
        ]}
        checkAllPermissions={true}
      >
        <PersonalizedCoachingInsights />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const overAllSentimentAnalysisReportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/sentiment-analysis-report",
  component: () => (
    <FeatureFlagGuard requiredFlags={["sentiment-analysis-report"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-sentiment-analysis-reports",
          "can-view-reports",
        ]}
        checkAllPermissions={true}
      >
        <OverAllSentimentAnalysisReport />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const onboardUserRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/onboard-user",
  component: OnboardUser,
});

export const inviteVerificationRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/invite-verification/$verificationKey",
  component: InviteVerification,
});

export const employeeToUserVerificationRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/employee-to-user-verification/$token",
  component: EmployeeToUserVerification,
});

export const roleSelectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/role-select",
  component: RoleSelect,
});

export const UnAuthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/un-authorized",
  component: UnAuthorized,
});

export const nanoLearningRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/nano-learning",
  component: () => (
    <FeatureFlagGuard requiredFlags={["nano-learning"]}>
      <RoutePermissionGuard allowedPermissions={["can-view-nano-learning"]}>
        <NanoLearning />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const createNanoLearningRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/create-nano-learning",
  component: () => (
    <FeatureFlagGuard requiredFlags={["nano-learning"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-nano-learning",
          "can-view-nano-learning-create-module",
        ]}
        checkAllPermissions={true}
      >
        <CreateNanoLearning />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const editNanoLearningRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/edit-nano-learning/$nanoLearningId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["nano-learning"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-edit-nano-learning-create-module",
          "can-view-nano-learning",
        ]}
        checkAllPermissions={true}
      >
        <EditNanoLearning />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const learningRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/learning",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={[
        "can-view-nano-learning",
        "can-view-nano-learning-browse-content",
      ]}
      checkAllPermissions={true}
    >
      <LearningLibrary />
    </RoutePermissionGuard>
  ),
});

export const learningTemplateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/learning/$templateId",
  component: LearningLayout,
});

export const categoryContentRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/learning/$templateId/$categoryId",
  component: CategoryContent,
});

export const moduleDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/learning/module/$moduleId",
  component: LearningLayout,
});

export const analyticsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-analytics-explorer"]}>
      <AnalyticsExplorer />
    </RoutePermissionGuard>
  ),
});

export const analyticsMainOuterRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics/dashboards",
  component: () => (
    <AnalyticsDashboardsListAccessGuard>
      <AnalyticsDashboardsListPage />
    </AnalyticsDashboardsListAccessGuard>
  ),
});

export const analyticsCreateDashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics/dashboards/new",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={["can-add-analytics-dashboard"]}
      checkAllPermissions={true}
    >
      <CreateDashboardPage />
    </RoutePermissionGuard>
  ),
});

export const analyticsDetailComposerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics/dashboards/$dashboardId",
  component: () => (
    <AnalyticsDashboardDetailAccessGuard>
      <AnalyticDashboardComposerPage />
    </AnalyticsDashboardDetailAccessGuard>
  ),
});

export const analyticsAdminMetricsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics/metrics",
  component: () => (
    <RoutePermissionGuard allowedPermissions={["can-view-analytics-metrics"]}>
      <MetricDefinitionPage />
    </RoutePermissionGuard>
  ),
});

export const analyticsAdminMetricNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics/metrics/new",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={["can-view-analytics-metrics"]}
      checkAllPermissions={true}
    >
      <MetricDefinitionForm />
    </RoutePermissionGuard>
  ),
});

export const analyticsAdminDerivedMetricNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics/derived-metrics/new",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={["can-add-analytics-derived-metric"]}
      checkAllPermissions={true}
    >
      <DerivedMetricForm />
    </RoutePermissionGuard>
  ),
});

export const analyticsAdminDimensionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/analytics/dimensions",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={["can-view-analytics-dimensions"]}
    >
      <DimensionDefinitionPage />
    </RoutePermissionGuard>
  ),
});

// Admin -Dashboard
export const tenantAdminDashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/dashboard",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={["can-view-admin-configurations"]}
    >
      <AdminDashboard />
    </RoutePermissionGuard>
  ),
});

// Admin- entity field mapping
export const entityFieldMappingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/entity-field-mapping",
  component: () => (
    <FeatureFlagGuard requiredFlags={["tenant-admin-entity-field-mapping"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-evaluation-field-configuration",
          "can-view-admin-configurations",
        ]}
        checkAllPermissions={true}
      >
        <EntityFieldMapping />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

// Admin- workFlow builder
export const workFlowBuilderRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/workflow-builder",
  component: () => (
    <FeatureFlagGuard requiredFlags={["tenant-admin-workflow-builder"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-workflow",
          "can-view-admin-configurations",
        ]}
        checkAllPermissions={true}
      >
        <WorkFLowBuilder />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});
// Admin- Campaigns
export const adminCampaignsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/campaigns",
  component: () => (
    <FeatureFlagGuard requiredFlags={["tenant-admin-campaigns"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-campaign-management",
          "can-view-campaigns",
          "can-view-admin-configurations",
        ]}
        checkAllPermissions={true}
      >
        <AdminCampaigns />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});
// Admin-Services
export const adminServicesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/services",
  component: () => (
    <FeatureFlagGuard requiredFlags={["tenant-admin-services"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-service-configuration",
          "can-view-services",
          "can-view-admin-configurations",
        ]}
        checkAllPermissions={true}
      >
        <AdminServices />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});
// Admin-Call Summary
export const adminCallSummaryRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/call-summary",
  component: () => (
    <FeatureFlagGuard
      requiredFlags={["tenant-admin-call-summary", "tenant-admin-dashboard"]}
    >
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-summary-configuration",
          "can-view-admin-configurations",
        ]}
        checkAllPermissions={true}
      >
        <AdminCallSummary />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

// Admin - AI Engine Configurations
export const aiEngineConfigurationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/ai-engine-configurations",
  component: () => (
    <FeatureFlagGuard requiredFlags={["tenant-admin-dashboard"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-ai-engine-configs-list",
          "can-view-admin-configurations",
        ]}
        checkAllPermissions={true}
      >
        <AIEngineConfigurations />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

// Admin-STT Engine Configuration
export const sttEngineConfigurationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/stt-engine-configuration",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={[
        "can-view-stt-engine-configs",
        "can-view-admin-configurations",
      ]}
      checkAllPermissions={true}
    >
      <STTEngineConfigurations />
    </RoutePermissionGuard>
  ),
});
// Admin -Domains
export const adminDomainsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/admin/domains",
  component: () => (
    <FeatureFlagGuard requiredFlags={["tenant-admin-domains"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-domain-configuration",
          "can-view-admin-configurations",
        ]}
        checkAllPermissions={true}
      >
        <AdminDomains />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

// Admin - date and location

export const agentPerformanceLeaderboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/agent-performance/leaderboard",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={[
        "can-view-agent-ranking-list",
        "can-view-agent-performance-leaderboard",
      ]}
      checkAllPermissions={true}
    >
      <AgentPerformanceLeaderboard />
    </RoutePermissionGuard>
  ),
});

export const aiImprovementTrackingDetails = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/ai-improvment-tracking-details/$agentId",
  component: () => (
    <RoutePermissionGuard
      allowedPermissions={[
        "can-view-agent-performance-ai-improvement-tracking-details",
      ]}
      checkAllPermissions={true}
    >
      <AIImprovementTrackingTable />
    </RoutePermissionGuard>
  ),
});

export const campaignTeamReportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/campaigns-wise-team-report",
  component: () => (
    <FeatureFlagGuard requiredFlags={["campaigns-wise-team-report"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-campaign-team-performance-report",
          "can-view-reports",
        ]}
        checkAllPermissions={true}
      >
        <CampaignTeamsReport />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const campaignTeamDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/campaign-team-detail/$campaignId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["campaigns-wise-team-report"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-campaign-team-performance-report",
          "can-view-campaign-team-performance-report-details",
          "can-view-reports",
        ]}
        checkAllPermissions={true}
      >
        <CampaignTeamDetail />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const supervisorTeamDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/supervisor-team-detail/$supervisorId",
  component: () => (
    <FeatureFlagGuard requiredFlags={["team-wise-report"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-supervisor-team-performance-report",
          "can-view-supervisor-team-performance-report-details",
          "can-view-reports",
        ]}
        checkAllPermissions={true}
      >
        <SupervisorTeamDetail />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const supervisorsTeamReportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/team-wise-report",
  component: () => (
    <FeatureFlagGuard requiredFlags={["team-wise-report"]}>
      <RoutePermissionGuard
        allowedPermissions={[
          "can-view-supervisor-team-performance-report",
          "can-view-reports",
        ]}
        checkAllPermissions={true}
      >
        <SupervisorTeamsReport />
      </RoutePermissionGuard>
    </FeatureFlagGuard>
  ),
});

export const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    MainLayoutRoute,
    failureCategoryCallsRoute,
    evaluationDashboardRoute,
    manageTemplateRoute,
    evaluationListingRoute,
    brandingRoute,
    tenantAdminDashboardRoute,
    entityFieldMappingRoute,
    workFlowBuilderRoute,
    adminDomainsRoute,
    adminCampaignsRoute,
    adminServicesRoute,
    adminCallSummaryRoute,
    aiEngineConfigurationsRoute,
    sttEngineConfigurationsRoute,
    evaluationDetailsRoute,
    addManageTemplateRoute,
    dataIngestionRoute,
    dataIngestionAddEntityRoute,
    pushApiClientsRoute,
    nanoLearningHubRoute,
    pushApiClientsHistoryRoute,
    manageTemplateEditRoute,
    manageUserRoute,
    dataIngestionEditEntityRoute,
    changePasswordRoute,
    UnAuthorizedRoute,
    entityScaleRatingRoute,
    organizationHierarchyRoute,
    piiConfigurationRoute,
    piiEntityRoute,
    piiPatternRoute,
    piiDenyTermRoute,
    selectionCriteriaRoute,
    callProcessingQueuePlannerRoute,
    topViolationByCategoryRoute,
    personalizedCoachingInsightsRoute,
    createEntityRoute,
    agentPerformanceListingRoute,
    agentPerformanceDetailsRoute,
    agentPerformanceDetailsListRoute,
    configDashboardRoute,
    servicesRoute,
    campaignRoute,
    employeeManagementRoute,
    assignEmployeePermissionRoute,
    employeeAttributesRoute,
    roleManagementRoute,
    assignPermissionRoute,
    customerInsightsRoute,
    customerInsightsStaticChartsRoute,
    nanoLearningRoute,
    createNanoLearningRoute,
    editNanoLearningRoute,
    learningRoute,
    learningTemplateRoute,
    categoryContentRoute,
    moduleDetailRoute,
    overAllSentimentAnalysisReportRoute,
    roleSelectRoute,
    agentRankingConfigurationRoute,
    reportsRoute,
    analyticsRoute,
    analyticsMainOuterRoute,
    analyticsCreateDashboardRoute,
    analyticsDetailComposerRoute,
    analyticsAdminMetricsRoute,
    analyticsAdminMetricNewRoute,
    analyticsAdminDerivedMetricNewRoute,
    analyticsAdminDimensionsRoute,
    agentPerformanceLeaderboardRoute,
    aiImprovementTrackingDetails,
    campaignTeamReportRoute,
    campaignTeamDetailRoute,
    supervisorTeamDetailRoute,
    supervisorsTeamReportRoute,
  ]),
  authLayoutRoute.addChildren([
    emptyRoute,
    loginRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    renewPasswordRoute,
    onboardUserRoute,
    inviteVerificationRoute,
    employeeToUserVerificationRoute,
  ]),
  updatePasswordRoute,
  NoPageFoundRoute,
  ForbiddenRoute,
]);
